define('presentation/common/scrollToOffsetBinding',[], function() {
    'use strict';

    const DEFAULT_OFFSET = 0;
    const DEFAULT_ACTIVE_PROPERTY = 'isScrollToOffsetActive';

    /**
     * Scrolls the bound (scrollable) element so that a target descendant comes to
     * rest `offset` pixels below the element's top edge, re-running each time the
     * watched view model is composed.
     *
     * This only moves the scroll position - the container keeps its padding, so
     * the user can scroll back up and see the whole page as authored.
     *
     * A composed view model opts in by exposing a truthy `isScrollToOffsetActive`
     * (override the property name with `activeProperty`). View models that do not
     * opt in fall back to being scrolled to the top, matching the behavior of
     * `resetScrollPositionOnChange`.
     *
     * Note: scrollTop cannot exceed `scrollHeight - clientHeight`. When a composed
     * page is short enough that the container barely overflows, the target settles
     * as close to the requested offset as the available scroll range allows.
     *
     * data-bind="scrollToOffset: {
     *                viewModel: childViewModel,
     *                targetSelector: '.some-header',
     *                offset: 20
     *            }"
     */
    return {
        init: function(element, valueAccessor) {
            const options = ko.unwrap(valueAccessor()) || {};
            const composedViewModel = options.viewModel;
            const targetSelector = options.targetSelector;
            const offset = options.offset === undefined ? DEFAULT_OFFSET : options.offset;
            const activeProperty = options.activeProperty || DEFAULT_ACTIVE_PROPERTY;

            if (ko.isObservable(composedViewModel) === false) {
                return;
            }

            let compositionSubscription = null;
            let pendingFrame = null;

            const cancelPendingScroll = () => {
                if (pendingFrame !== null) {
                    window.cancelAnimationFrame(pendingFrame);
                    pendingFrame = null;
                }
            };

            const findTarget = (viewModel) => {
                if (targetSelector === undefined || targetSelector === null || targetSelector === '') {
                    return null;
                }

                if (ko.unwrap(viewModel[activeProperty]) !== true) {
                    return null;
                }

                return element.querySelector(targetSelector);
            };

            // Measured on the next frame: when this runs off the composition
            // observable the browser has not necessarily laid the new step out yet,
            // and an early measurement reads the outgoing page's geometry.
            const scrollToOffset = (viewModel) => {
                cancelPendingScroll();

                pendingFrame = window.requestAnimationFrame(() => {
                    pendingFrame = null;

                    const target = findTarget(viewModel);
                    if (target === null) {
                        element.scrollTop = 0;
                        return;
                    }

                    const distanceFromContentTop = target.getBoundingClientRect().top -
                        element.getBoundingClientRect().top +
                        element.scrollTop;

                    element.scrollTop = Math.max(distanceFromContentTop - offset, 0);
                });
            };

            const scrollWhenComposed = (viewModel) => {
                if (compositionSubscription !== null) {
                    compositionSubscription.dispose();
                    compositionSubscription = null;
                }

                cancelPendingScroll();

                if (viewModel === null || viewModel === undefined) {
                    return;
                }

                const isCompositionComplete = viewModel.isCompositionComplete;
                if (ko.isObservable(isCompositionComplete) === false) {
                    scrollToOffset(viewModel);
                    return;
                }

                if (isCompositionComplete()) {
                    scrollToOffset(viewModel);
                    return;
                }

                compositionSubscription = isCompositionComplete.subscribe(function(isComplete) {
                    if (isComplete) {
                        scrollToOffset(viewModel);
                    }
                });
            };

            const viewModelSubscription = composedViewModel.subscribe(scrollWhenComposed);
            scrollWhenComposed(composedViewModel());

            ko.utils.domNodeDisposal.addDisposeCallback(element, function() {
                viewModelSubscription.dispose();
                cancelPendingScroll();
                if (compositionSubscription !== null) {
                    compositionSubscription.dispose();
                }
            });
        }
    };
});

